
import React from 'react';
import { Edit, Trash2, Send, BringToFront, Settings, FileText } from 'lucide-react';

interface DiagramContextMenuProps {
  position: { x: number; y: number };
  type: 'node' | 'edge' | 'group' | 'text' | 'actor' | 'image' | null;
  nodeData?: any; // Dados do node para verificar se é um componente
  isMultipleSelection?: boolean; // Nova prop para indicar múltipla seleção
  selectedCount?: number; // Quantidade de elementos selecionados
  onEdit: () => void;
  onEditComponent?: () => void; // Nova prop para editar componente
  onViewComponentDetails?: () => void; // Nova prop para ver detalhes do componente
  onDelete: () => void;
  onSendToBack: () => void;
  onBringToFront: () => void;
  onClose: () => void;
}

export function DiagramContextMenu({
  position,
  type,
  nodeData,
  isMultipleSelection = false,
  selectedCount = 0,
  onEdit,
  onEditComponent,
  onViewComponentDetails,
  onDelete,
  onSendToBack,
  onBringToFront,
  onClose,
}: DiagramContextMenuProps) {
  if (!type) return null;

  const menuStyle: React.CSSProperties = {
    position: 'fixed',
    left: position.x,
    top: position.y,
    zIndex: 1000,
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    minWidth: '150px',
    padding: '4px 0',
  };

  const itemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    cursor: 'pointer',
    fontSize: '14px',
    border: 'none',
    background: 'none',
    width: '100%',
    textAlign: 'left',
  };

  const deleteItemStyle: React.CSSProperties = {
    ...itemStyle,
    color: '#dc2626',
  };

  // Verifica se o node é um componente (tem selectedComponentId e não é 'novo')
  const isComponentNode = type === 'node' && nodeData?.selectedComponentId && nodeData.selectedComponentId !== 'novo';

  return (
    <div 
      style={menuStyle} 
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* Para múltipla seleção, mostrar apenas ações comuns */}
      {isMultipleSelection && (
        <div style={{ ...itemStyle, cursor: 'default', color: '#6b7280', fontWeight: '500' }}>
          {selectedCount} elementos selecionados
        </div>
      )}
      
      {/* Opções de edição só aparecem para seleção única */}
      {!isMultipleSelection && (type === 'edge' || type === 'text' || type === 'group' || type === 'actor') && (
        <button
          style={itemStyle}
          onClick={onEdit}
          onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f3f4f6'}
          onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
        >
          <Edit size={16} />
          <span>{type === 'edge' ? 'Editar conexão' : type === 'group' ? 'Editar moldura' : type === 'actor' ? 'Editar Ator' : 'Editar'}</span>
        </button>
      )}

      {/* Opção "Editar Componente" apenas para seleção única de nodes que são componentes */}
      {!isMultipleSelection && isComponentNode && onEditComponent && (
        <button
          style={itemStyle}
          onClick={onEditComponent}
          onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f3f4f6'}
          onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
        >
          <Settings size={16} />
          <span>Editar Componente</span>
        </button>
      )}

      {/* Opção "Ver detalhes" apenas para seleção única de nodes que são componentes */}
      {!isMultipleSelection && isComponentNode && onViewComponentDetails && (
        <button
          style={itemStyle}
          onClick={onViewComponentDetails}
          onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f3f4f6'}
          onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
        >
          <FileText size={16} />
          <span>Ver detalhes</span>
        </button>
      )}
      
      {/* Layering só para nodes, groups, actors e images */}
      {(type === 'node' || type === 'group' || type === 'actor' || type === 'image') && (
        <>
          <button
            style={itemStyle}
            onClick={onBringToFront}
            onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f3f4f6'}
            onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
          >
            <BringToFront size={16} />
            <span>Trazer pra frente</span>
          </button>
          
          <button
            style={itemStyle}
            onClick={onSendToBack}
            onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f3f4f6'}
            onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
          >
            <Send size={16} />
            <span>Enviar pra trás</span>
          </button>
        </>
      )}

      <button
        style={deleteItemStyle}
        onClick={onDelete}
        onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#fef2f2'}
        onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
      >
        <Trash2 size={16} />
        <span>{isMultipleSelection ? `Apagar selecionados` : 'Apagar'}</span>
      </button>
    </div>
  );
}
